import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  LuBuilding2,
  LuTarget,
  LuUsers,
  LuDollarSign,
  LuTrendingUp,
  LuActivity,
  LuCalendar,
  LuArrowRight
} from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const Dashboard = () => {
  const { userProfile } = useAuth();
  const [stats, setStats] = useState({
    totalAccounts: 0,
    totalContacts: 0,
    activeOpportunities: 0,
    totalRevenue: 0,
    wonOpportunities: 0,
    lostOpportunities: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('🔵 [Dashboard] useEffect triggered', {
      hasUserProfile: !!userProfile,
      changed_at: new Date().toISOString()
    });

    if (userProfile) {
      console.log('🔵 [Dashboard] Fetching dashboard data...');
      fetchDashboardData();
    } else {
      console.log('⚪ [Dashboard] No user profile yet, waiting...');
    }
  }, [userProfile]);

  const fetchDashboardData = async () => {
    try {
      console.log('🔵 [Dashboard] Starting data fetch...');
      setLoading(true);

      // Fetch counts
      console.log('🔵 [Dashboard] Fetching accounts, contacts, opportunities...');
      const [accountsRes, contactsRes, opportunitiesRes] = await Promise.all([
        supabase.from('accounts').select('id', { count: 'exact', head: true }),
        supabase.from('contacts').select('id', { count: 'exact', head: true }),
        supabase.from('opportunities').select('*')
      ]);

      console.log('🔵 [Dashboard] Query results:', {
        accountsCount: accountsRes.count,
        accountsError: accountsRes.error?.message,
        contactsCount: contactsRes.count,
        contactsError: contactsRes.error?.message,
        opportunitiesCount: opportunitiesRes.data?.length,
        opportunitiesError: opportunitiesRes.error?.message
      });

      if (accountsRes.error) throw accountsRes.error;
      if (contactsRes.error) throw contactsRes.error;
      if (opportunitiesRes.error) throw opportunitiesRes.error;

      const opportunities = opportunitiesRes.data || [];

      // Calculate stats
      const activeOpps = opportunities.filter(
        opp => !['Closed Won', 'Closed Lost'].includes(opp.stage)
      );
      const wonOpps = opportunities.filter(opp => opp.stage === 'Closed Won');
      const lostOpps = opportunities.filter(opp => opp.stage === 'Closed Lost');

      const totalRevenue = wonOpps.reduce(
        (sum, opp) => sum + (parseFloat(opp.amount) || 0),
        0
      );

      const calculatedStats = {
        totalAccounts: accountsRes.count || 0,
        totalContacts: contactsRes.count || 0,
        activeOpportunities: activeOpps.length,
        totalRevenue,
        wonOpportunities: wonOpps.length,
        lostOpportunities: lostOpps.length
      };

      console.log('✅ [Dashboard] Stats calculated:', calculatedStats);
      setStats(calculatedStats);

      // Fetch recent activity (opportunity history)
      console.log('🔵 [Dashboard] Fetching recent activity...');
      const { data: historyData, error: historyError } = await supabase
        .from('opportunity_history')
        .select(`
          *,
          opportunities (
            name,
            accounts (
              name
            )
          ),
          users (
            full_name
          )
        `)
        .order('changed_at', { ascending: false })
        .limit(5);

      console.log('🔵 [Dashboard] Activity query result:', {
        historyCount: historyData?.length,
        historyError: historyError?.message
      });

      if (historyError) throw historyError;

      setRecentActivity(historyData || []);
      setError('');
      console.log('✅ [Dashboard] Data fetch complete');
    } catch (err) {
      console.error('❌ [Dashboard] Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      console.log('✅ [Dashboard] Setting loading to false');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStageColor = (stage) => {
    const colors = {
      'Lead': 'text-gray-600',
      'Qualified': 'text-blue-600',
      'Proposal': 'text-yellow-600',
      'Negotiation': 'text-orange-600',
      'Closed Won': 'text-green-600',
      'Closed Lost': 'text-red-600'
    };
    return colors[stage] || 'text-gray-600';
  };

  console.log('🔵 [Dashboard] Render state:', { loading, error, hasStats: !!stats });

  if (loading) {
    console.log('⏳ [Dashboard] Showing loading spinner');
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    console.log('❌ [Dashboard] Showing error message');
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  console.log('✅ [Dashboard] Rendering dashboard content');
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="mt-1 text-sm text-gray-600">
          Welcome back,{userProfile?.full_name}! Here's what's happening with your CRM today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Accounts */}
        <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SafeIcon
                  icon={LuBuilding2}
                  className="h-8 w-8 text-blue-600"
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Accounts
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stats.totalAccounts}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 px-5 py-3">
            <div className="text-sm">
              <span className="text-blue-700 font-medium">Shared across all teams</span>
            </div>
          </div>
        </div>

        {/* Active Opportunities */}
        <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SafeIcon
                  icon={LuTarget}
                  className="h-8 w-8 text-green-600"
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Opportunities
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stats.activeOpportunities}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-green-50 px-5 py-3">
            <div className="text-sm flex items-center justify-between">
              <span className="text-green-700 font-medium">
                {stats.wonOpportunities} won
              </span>
              <span className="text-red-700 font-medium">
                {stats.lostOpportunities} lost
              </span>
            </div>
          </div>
        </div>

        {/* Total Contacts */}
        <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SafeIcon
                  icon={LuUsers}
                  className="h-8 w-8 text-yellow-600"
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Contacts
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stats.totalContacts}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 px-5 py-3">
            <div className="text-sm">
              <span className="text-yellow-700 font-medium">With privacy controls</span>
            </div>
          </div>
        </div>

        {/* Revenue Pipeline */}
        <div className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <SafeIcon
                  icon={LuDollarSign}
                  className="h-8 w-8 text-purple-600"
                />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Revenue
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatCurrency(stats.totalRevenue)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 px-5 py-3">
            <div className="text-sm">
              <span className="text-purple-700 font-medium flex items-center">
                <SafeIcon icon={LuTrendingUp} className="h-4 w-4 mr-1" />
                From closed won deals
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <SafeIcon icon={LuActivity} className="h-5 w-5 mr-2 text-gray-400" />
              Recent Activity
            </h3>
            <span className="text-sm text-gray-500">Last 5 changes</span>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {recentActivity.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <SafeIcon icon={LuActivity} className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start working on opportunities to see activity here
              </p>
            </div>
          ) : (
            recentActivity.map((activity) => (
              <div key={activity.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <SafeIcon icon={LuTarget} className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">{activity.users?.full_name}</span>
                      {' '}moved{' '}
                      <span className="font-medium">{activity.opportunities?.name}</span>
                      {' '}from{' '}
                      <span className={`font-medium ${getStageColor(activity.old_stage)}`}>
                        {activity.old_stage}
                      </span>
                      {' '}to{' '}
                      <span className={`font-medium ${getStageColor(activity.new_stage)}`}>
                        {activity.new_stage}
                      </span>
                    </p>
                    <div className="mt-1 flex items-center space-x-2 text-xs text-gray-500">
                      <SafeIcon icon={LuCalendar} className="h-3 w-3" />
                      <span>{format(new Date(activity.changed_at), 'MMM d,yyyy h:mm a')}</span>
                      {activity.opportunities?.accounts?.name && (
                        <>
                          <span>•</span>
                          <span>{activity.opportunities.accounts.name}</span>
                        </>
                      )}
                    </div>
                    {activity.notes && (
                      <p className="mt-1 text-xs text-gray-600">{activity.notes}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {recentActivity.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 text-right">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-500 flex items-center justify-end w-full">
              View all activity
              <SafeIcon icon={LuArrowRight} className="ml-1 h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={LuBuilding2} className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Add Account</span>
          </button>
          <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={LuUsers} className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Add Contact</span>
          </button>
          <button className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <SafeIcon icon={LuTarget} className="h-5 w-5 text-gray-600 mr-2" />
            <span className="text-sm font-medium text-gray-700">Add Opportunity</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;